import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { volumesAPI, articlesAPI } from '../lib/api';
import { Badge } from '../components/ui/badge';
import { RatingSummary } from '../components/StarRating';
import { ChevronRight, BookOpen, Star, MessageSquare } from 'lucide-react';

export default function ArchivePage() {
  const [volumes, setVolumes] = useState([]);
  const [articles, setArticles] = useState([]);
  const [featured, setFeatured] = useState([]);
  const [selectedVol, setSelectedVol] = useState(null);
  const [selectedNum, setSelectedNum] = useState(null);

  useEffect(() => {
    volumesAPI.list().then(res => setVolumes(res.data)).catch(() => {});
    articlesAPI.list().then(res => setArticles(res.data.items || res.data)).catch(() => {});
    articlesAPI.featured().then(res => setFeatured((res.data || []).filter(a => a.featured))).catch(() => {});
  }, []);

  const filteredArticles = selectedVol !== null
    ? articles.filter(a => a.volumen === selectedVol && (selectedNum === null || a.numero === selectedNum))
    : articles;

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16" data-testid="archive-page">
      <div className="animate-fade-up">
        <div className="flex items-center gap-3 mb-2">
          <div className="h-0.5 w-8 bg-trans-blue rounded-full" />
          <span className="text-xs font-sans font-semibold tracking-widest uppercase text-trans-blue">Publicaciones</span>
        </div>
        <h1 className="text-4xl sm:text-5xl font-serif font-bold text-slate-dark">Archivo</h1>
        <p className="text-base font-sans text-gray-400 mt-3 max-w-lg">
          Explora todas las publicaciones de Archivos Latinoamericanos de Estudios Trans.
        </p>
      </div>

      {/* Volumes filter */}
      {volumes.length > 0 && (
        <div className="flex flex-wrap gap-2 mt-8" data-testid="volume-filters">
          <button
            onClick={() => { setSelectedVol(null); setSelectedNum(null); }}
            className={`px-4 py-2 text-sm font-sans rounded-md border transition-colors ${
              selectedVol === null ? 'bg-slate-dark text-white border-slate-dark' : 'bg-white text-gray-600 border-gray-200 hover:border-gray-300'
            }`}
            data-testid="filter-all"
          >
            Todos
          </button>
          {volumes.map(v => (
            <button
              key={`${v.volumen}-${v.numero}`}
              onClick={() => { setSelectedVol(v.volumen); setSelectedNum(v.numero); }}
              className={`px-4 py-2 text-sm font-sans rounded-md border transition-colors ${
                selectedVol === v.volumen && selectedNum === v.numero ? 'bg-slate-dark text-white border-slate-dark' : 'bg-white text-gray-600 border-gray-200 hover:border-gray-300'
              }`}
              data-testid={`filter-vol-${v.volumen}-${v.numero}`}
            >
              Vol. {v.volumen} N. {v.numero} <span className="text-xs opacity-60">({v.article_count})</span>
            </button>
          ))}
        </div>
      )}

      {/* Featured strip (admin-curated) */}
      {selectedVol === null && featured.length > 0 && (
        <section className="mt-10" data-testid="archive-featured">
          <div className="flex items-center gap-2 mb-4">
            <Star className="w-4 h-4 text-amber-500" fill="currentColor" />
            <h2 className="text-base md:text-lg font-serif font-semibold text-slate-dark">Papers destacados</h2>
            <span className="text-xs font-sans text-gray-400">Seleccion del equipo editorial</span>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
            {featured.map((article, i) => (
              <Link
                key={article.id}
                to={`/articulo/${article.id}`}
                className="group block p-5 bg-amber-50/40 border border-amber-200 rounded-sm hover:border-amber-300 hover:shadow-sm transition-all duration-200"
                data-testid={`archive-featured-${i}`}
              >
                <div className="flex items-center gap-2 mb-2">
                  <Badge variant="outline" className="text-[10px] font-sans bg-white text-blue-700 border-blue-100">Vol. {article.volumen} N. {article.numero}</Badge>
                  {article.categoria && <span className="text-[10px] font-sans text-gray-400">{article.categoria}</span>}
                </div>
                <h3 className="font-serif font-semibold text-slate-dark group-hover:text-trans-blue transition-colors leading-snug line-clamp-2">{article.titulo}</h3>
                <p className="text-xs font-sans text-gray-400 mt-1">{article.autores}</p>
                <div className="flex items-center justify-between mt-3">
                  <RatingSummary avg={article.rating_avg} count={article.rating_count} testId={`archive-featured-rating-${i}`} />
                  <span className="flex items-center gap-1 text-trans-blue text-xs font-sans font-medium">Leer <ChevronRight className="w-3 h-3" /></span>
                </div>
              </Link>
            ))}
          </div>
          <div className="h-px bg-gray-100 mt-10" />
        </section>
      )}

      {/* Articles list */}
      <div className="mt-10 space-y-4">
        {filteredArticles.length === 0 ? (
          <div className="text-center py-16">
            <BookOpen className="w-12 h-12 text-gray-300 mx-auto mb-4" strokeWidth={1} />
            <p className="text-gray-400 font-sans">No hay articulos publicados aun.</p>
          </div>
        ) : (
          filteredArticles.map((article, i) => (
            <Link
              key={article.id}
              to={`/articulo/${article.id}`}
              className="group block p-6 bg-white border border-gray-100 rounded-sm hover:border-gray-200 hover:shadow-sm transition-all duration-200"
              data-testid={`archive-article-${i}`}
            >
              <div className="flex flex-col md:flex-row md:items-start gap-4">
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-2">
                    <Badge variant="outline" className="text-[10px] font-sans bg-blue-50 text-blue-700 border-blue-100">
                      Vol. {article.volumen} N. {article.numero}
                    </Badge>
                    {article.categoria && (
                      <Badge variant="outline" className="text-[10px] font-sans bg-pink-50 text-pink-700 border-pink-100">
                        {article.categoria}
                      </Badge>
                    )}
                    {article.doi && (
                      <span className="text-[10px] font-sans text-gray-400">DOI: {article.doi}</span>
                    )}
                    {article.featured && (
                      <span className="inline-flex items-center gap-1 text-[10px] font-sans font-semibold text-amber-600"><Star className="w-3 h-3" fill="currentColor" /> Destacado</span>
                    )}
                  </div>
                  <h3 className="font-serif font-semibold text-lg text-slate-dark group-hover:text-trans-blue transition-colors leading-snug">
                    {article.titulo}
                  </h3>
                  <p className="text-sm font-sans text-gray-400 mt-1">{article.autores}</p>
                  <p className="text-sm font-serif text-gray-500 mt-2 leading-relaxed line-clamp-2">{article.abstract_es}</p>
                  {article.palabras_clave?.length > 0 && (
                    <div className="flex flex-wrap gap-1 mt-3">
                      {article.palabras_clave.map(kw => (
                        <span key={kw} className="text-[10px] font-sans px-2 py-0.5 rounded-sm bg-gray-50 text-gray-500">{kw}</span>
                      ))}
                    </div>
                  )}
                  {(article.rating_count > 0 || article.comments_count > 0) && (
                    <div className="flex items-center gap-4 mt-3">
                      <RatingSummary avg={article.rating_avg} count={article.rating_count} testId={`archive-rating-${i}`} />
                      {article.comments_count > 0 && (
                        <span className="inline-flex items-center gap-1 text-xs font-sans text-gray-400"><MessageSquare className="w-3.5 h-3.5" strokeWidth={1.5} /> {article.comments_count}</span>
                      )}
                    </div>
                  )}
                </div>
                <div className="flex items-center gap-1 text-trans-blue text-sm font-sans font-medium shrink-0">
                  Leer <ChevronRight className="w-3 h-3" />
                </div>
              </div>
            </Link>
          ))
        )}
      </div>
    </div>
  );
}
